/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Entity } from '@backstage/catalog-model';
import { z as zod } from 'zod';
import { makeFieldSchema } from '../../../extensions/types';

export const createEntityQueryFilterExpressionSchema = (z: typeof zod) =>
  z.record(
    z
      .string()
      .or(z.object({ exists: z.boolean().optional() }))
      .or(z.array(z.string())),
  );

/**
 * @public
 */
export const EntityPickerFieldSchema = makeFieldSchema({
  output: z => z.union([z.string(), z.custom<Entity>(() => true)]),
  uiOptions: z =>
    z.object({
      /**
       * @deprecated Use `catalogFilter` instead.
       */
      allowedKinds: z
        .array(z.string())
        .optional()
        .describe(
          'DEPRECATED: Use `catalogFilter` instead. List of kinds of entities to derive options from',
        ),
      defaultKind: z
        .string()
        .optional()
        .describe(
          'The default entity kind. Options of this kind will not be prefixed.',
        ),
      allowArbitraryValues: z
        .boolean()
        .optional()
        .describe('Whether to allow arbitrary user input. Defaults to true'),
      defaultNamespace: z
        .union([z.string(), z.literal(false)])
        .optional()
        .describe(
          'The default namespace. Options with this namespace will not be prefixed.',
        ),
      catalogFilter: (t => t.or(t.array()))(
        createEntityQueryFilterExpressionSchema(z),
      )
        .optional()
        .describe('List of key-value filter expression for entities'),
      excludeValues: z
        .array(z.string())
        .optional()
        .describe('List of values to exclude from the options'),
      showOnlyUserOwnGroups: z
        .boolean()
        .optional()
        .describe('Whether to show only user own groups. Defaults to false'),
      storeRawEntity: z
        .boolean()
        .optional()
        .describe(
          'Tells the EntityPicker to store the whole entity in the formContext instead of just the entityRef. This is useful when combined with the DescriptorPicker, so that the user can wrangle a descriptor loaded by the EntityPicker',
        ),
      'ui:widget': z
        .string()
        .optional()
        .describe('The widget to use for the field'),
    }),
});

/**
 * The input props that can be specified under `ui:options` for the
 * `EntityPicker` field extension.
 *
 * @public
 */
export type EntityPickerUiOptions = NonNullable<
  typeof EntityPickerFieldSchema.TProps.uiSchema['ui:options']
>;

export type EntityPickerProps = typeof EntityPickerFieldSchema.TProps;

export const EntityPickerSchema = EntityPickerFieldSchema.schema;

export type EntityPickerFilterQuery = zod.TypeOf<
  ReturnType<typeof createEntityQueryFilterExpressionSchema>
>;

export type EntityPickerFilterQueryValue =
  EntityPickerFilterQuery[keyof EntityPickerFilterQuery];
